package dev.lingvo.shared

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/** A width and height in pixels. */
@Serializable
data class PixelSize(val width: Int, val height: Int) {
    fun rotated(): PixelSize = PixelSize(height, width)

    /** `1320×2868`, the spelling every refusal uses. */
    val label: String get() = "$width×$height"
}

/** Long side over short side as a fraction, so every rule is exact integer arithmetic. */
@Serializable
data class Ratio(val num: Int, val den: Int)

/** What sizes an image may have. Two kinds, because Apple names sizes and Google names ranges. */
@Serializable
sealed interface SizeRule {
    /** One of these sizes; [rotatable] also accepts each of them rotated (landscape). */
    @Serializable
    @SerialName("exact")
    data class Exact(val sizes: List<PixelSize>, val rotatable: Boolean = true) : SizeRule

    /**
     * Each side in [minSide]..[maxSide]; long/short at most [maxLongToShort] when set; and, when
     * [aspects] is not empty, `width * a.height == height * a.width` for one of them.
     */
    @Serializable
    @SerialName("range")
    data class Range(
        val minSide: Int,
        val maxSide: Int,
        val maxLongToShort: Ratio? = null,
        val aspects: List<PixelSize> = emptyList(),
    ) : SizeRule
}

/**
 * What an image is for. A **capture** is raw input to composition; a **store** image goes to the
 * store as it is. They have different rules: a Pixel-style 1080×2400 capture is fine as input and
 * over Google Play's 2:1 limit as a store image.
 */
@Serializable
enum class ShotPurpose { CAPTURE, STORE }

/**
 * A store output family: screenshots or a fixed listing asset such as the Play feature graphic.
 *
 * **Apple's classes are keyed by display class, not inch size.** App Store Connect's screenshot
 * specification names display classes ("Dynamic Island (medium)") and the App Asset Library API
 * names them too (`IPHONE_DYNAMIC_ISLAND_MEDIUM_DISPLAY`); the old inch labels and the deprecated
 * `ScreenshotDisplayType` names (`APP_IPHONE_67`; there is no `APP_IPHONE_69`) no longer describe
 * what the store asks for. [id] is the CLI/path spelling; [aliases] keep the inch spellings that
 * earlier drafts of the agent surface used (`iphone_69`, `android_phone`) parseable.
 */
@Serializable
enum class DeviceClass(
    val id: String,
    val platform: StorePlatform,
    val displayName: String,
    val aliases: List<String> = emptyList(),
) {
    IPHONE_DI_LARGE("iphone_di_large", StorePlatform.APP_STORE, "iPhone with Dynamic Island (large)", listOf("iphone_69", "iphone_67")),
    IPHONE_DI_MEDIUM("iphone_di_medium", StorePlatform.APP_STORE, "iPhone with Dynamic Island (medium)", listOf("iphone_63")),
    IPHONE_FACE_ID_LARGE("iphone_faceid_large", StorePlatform.APP_STORE, "iPhone with Face ID (large)", listOf("iphone_65")),
    IPHONE_FACE_ID_MEDIUM("iphone_faceid_medium", StorePlatform.APP_STORE, "iPhone with Face ID (medium)", listOf("iphone_61", "iphone_58")),
    IPHONE_HOME_BUTTON("iphone_home_button", StorePlatform.APP_STORE, "iPhone with Home button", listOf("iphone_55")),
    IPHONE_DUO("iphone_duo", StorePlatform.APP_STORE, "iPhone Duo"),
    IPAD_13("ipad_13", StorePlatform.APP_STORE, "iPad 13″", listOf("ipad_129")),
    IPAD_11("ipad_11", StorePlatform.APP_STORE, "iPad 11″"),
    MAC("mac", StorePlatform.APP_STORE, "Mac"),
    PLAY_PHONE("phone", StorePlatform.GOOGLE_PLAY, "Phone", listOf("android_phone")),
    PLAY_TABLET_7("tablet_7", StorePlatform.GOOGLE_PLAY, "7-inch tablet", listOf("android_tablet_7")),
    PLAY_TABLET_10("tablet_10", StorePlatform.GOOGLE_PLAY, "10-inch tablet", listOf("android_tablet_10")),
    PLAY_WEAR("wear", StorePlatform.GOOGLE_PLAY, "Wear OS", listOf("android_wear")),
    PLAY_FEATURE_GRAPHIC("feature_graphic", StorePlatform.GOOGLE_PLAY, "Feature graphic"),
    ;

    val spec: ShotSpec get() = ScreenshotSpecs.of(this)

    companion object {
        /** By [id], enum name or alias, ignoring case. */
        fun fromId(raw: String): DeviceClass? = entries.firstOrNull { device ->
            device.id.equals(raw, ignoreCase = true) || device.name.equals(raw, ignoreCase = true) ||
                device.aliases.any { it.equals(raw, ignoreCase = true) }
        }

        fun forPlatform(platform: StorePlatform): List<DeviceClass> = entries.filter { it.platform == platform }
    }
}

/** Everything a store says about one device class, plus our own cap. */
@Serializable
data class ShotSpec(
    val deviceClass: DeviceClass,
    val captureRule: SizeRule,
    val storeRule: SizeRule,
    /**
     * Apple: the App Asset Library display class (Apple matches each file to a spec itself, so one
     * value per class, not per size). Google: the `AppImageType` of `edits.images.upload`.
     */
    val storeType: String,
    /** False while [storeType] is our best reading and not a name seen in Apple's or Google's docs. */
    val storeTypeVerified: Boolean,
    val maxPerLocale: Int,
    val minPerLocale: Int,
    /** A store page for this platform cannot go live without it. */
    val required: Boolean,
    /** When it is required only for some apps ("the app runs on iPad"). */
    val requiredWhen: String? = null,
    /** The class the store scales down from when this one is absent. */
    val fallbackTo: DeviceClass? = null,
    val alphaAllowedInStore: Boolean = false,
    val maxBytes: Int = ScreenshotSpecs.MAX_IMAGE_BYTES,
    /** Advice, never a refusal. */
    val advice: String? = null,
    /** Where the numbers come from, and the day they were read. */
    val source: String,
)

/**
 * The size table, as a dated fallback.
 *
 * Read from the stores' published documentation on [TABLE_DATE]. Apple's real specs live in
 * `appAssetLibraryRefData` and change "without an API version change"; track E fills them at runtime
 * and this table is what holds when it has not. Every correction is one edit in [of].
 */
object ScreenshotSpecs {
    const val MAX_IMAGE_BYTES = 8 * 1024 * 1024
    const val MAX_SIDE_PX = 8192
    /** Above 7680×4320 (33.2 MP), the largest size any store accepts. */
    const val MAX_PIXELS = 40_000_000L
    const val TABLE_DATE = "2026-10-22"

    private const val APPLE_SOURCE =
        "App Store Connect Help, Screenshot specifications; App Store Connect API, AppAssetLibraryDisplayClass (read $TABLE_DATE)"
    private const val PLAY_SOURCE =
        "Play Console Help 9866151; Android Publisher API AppImageType (read $TABLE_DATE)"

    private val FORMATS_NOTE = "PNG (8-bit RGB or palette) or JPEG (8-bit YCbCr), no alpha."

    private fun apple(vararg sizes: PixelSize, rotatable: Boolean = true) = SizeRule.Exact(sizes.toList(), rotatable)

    private fun appleSpec(
        device: DeviceClass,
        rule: SizeRule,
        storeType: String,
        verified: Boolean,
        required: Boolean = false,
        requiredWhen: String? = null,
        fallback: DeviceClass? = null,
        advice: String? = null,
    ) = ShotSpec(
        deviceClass = device, captureRule = rule, storeRule = rule, storeType = storeType,
        storeTypeVerified = verified, maxPerLocale = 10, minPerLocale = 1, required = required,
        requiredWhen = requiredWhen, fallbackTo = fallback, advice = advice, source = APPLE_SOURCE,
    )

    private val PHONE_RANGE = SizeRule.Range(320, 3840, Ratio(2, 1))
    private val TABLET_STORE = SizeRule.Range(1080, 7680, aspects = listOf(PixelSize(9, 16), PixelSize(16, 9)))
    private val TABLET_CAPTURE = SizeRule.Range(1080, 7680, Ratio(2, 1))
    private const val PLAY_ADVICE =
        "Play features apps with at least 4 screenshots of at least 1080 px (9:16 portrait or 16:9 landscape)."

    private val table: Map<DeviceClass, ShotSpec> = DeviceClass.entries.associateWith { device ->
        when (device) {
            DeviceClass.IPHONE_DI_LARGE -> appleSpec(
                device, apple(PixelSize(1320, 2868), PixelSize(1290, 2796), PixelSize(1260, 2736)),
                "IPHONE_DYNAMIC_ISLAND_LARGE_DISPLAY", verified = true, fallback = DeviceClass.IPHONE_DI_MEDIUM,
            )
            DeviceClass.IPHONE_DI_MEDIUM -> appleSpec(
                device, apple(PixelSize(1206, 2622), PixelSize(1179, 2556)),
                "IPHONE_DYNAMIC_ISLAND_MEDIUM_DISPLAY", verified = true, required = true,
                advice = "Apple requires at least one iPhone screenshot in this class.",
            )
            DeviceClass.IPHONE_FACE_ID_LARGE -> appleSpec(
                device, apple(PixelSize(1284, 2778), PixelSize(1242, 2688)),
                "IPHONE_FACE_ID_LARGE_DISPLAY", verified = true, fallback = DeviceClass.IPHONE_DI_MEDIUM,
            )
            // VERIFY: display-class name not seen in Apple's published enum page (listed as "…").
            DeviceClass.IPHONE_FACE_ID_MEDIUM -> appleSpec(
                device, apple(PixelSize(1170, 2532), PixelSize(1125, 2436), PixelSize(1080, 2340)),
                "IPHONE_FACE_ID_MEDIUM_DISPLAY", verified = false, fallback = DeviceClass.IPHONE_DI_MEDIUM,
            )
            // VERIFY: display-class name.
            DeviceClass.IPHONE_HOME_BUTTON -> appleSpec(
                device, apple(PixelSize(1242, 2208)),
                "IPHONE_HOME_BUTTON_DISPLAY", verified = false, fallback = DeviceClass.IPHONE_DI_MEDIUM,
            )
            // Outer and inner screen. VERIFY: whether both sizes share one placement group.
            DeviceClass.IPHONE_DUO -> appleSpec(
                device, apple(PixelSize(1398, 2034), PixelSize(2007, 2853)),
                "IPHONE_DUO", verified = true,
                requiredWhen = "from April 2027, for apps built with the iOS 27.1 SDK or later",
                advice = "Required from April 2027 for apps built with the iOS 27.1 SDK or later; " +
                    "exists only in the App Asset Library.",
            )
            DeviceClass.IPAD_13 -> appleSpec(
                device, apple(PixelSize(2064, 2752), PixelSize(2048, 2732)),
                "IPAD_13_DISPLAY", verified = true, requiredWhen = "the app runs on iPad",
            )
            DeviceClass.IPAD_11 -> appleSpec(
                device, apple(PixelSize(1488, 2266), PixelSize(1668, 2420), PixelSize(1668, 2388), PixelSize(1640, 2360)),
                "IPAD_11_DISPLAY", verified = true, fallback = DeviceClass.IPAD_13,
            )
            // VERIFY: display-class name.
            DeviceClass.MAC -> appleSpec(
                device,
                apple(PixelSize(1280, 800), PixelSize(1440, 900), PixelSize(2560, 1600), PixelSize(2880, 1800), rotatable = false),
                "MAC_DISPLAY", verified = false, requiredWhen = "the app is a Mac app",
            )
            DeviceClass.PLAY_PHONE -> ShotSpec(
                device,
                // A raw capture of a 20:9 phone is accepted as input; the store image must be ≤ 2:1.
                captureRule = SizeRule.Range(320, 3840, Ratio(5, 2)),
                // VERIFY (Q8): Google's wording "max dimension can't be more than twice as long as min".
                storeRule = PHONE_RANGE,
                storeType = "phoneScreenshots", storeTypeVerified = true, maxPerLocale = 8, minPerLocale = 0,
                required = true, requiredWhen = "two screenshots across all device types",
                advice = "$PLAY_ADVICE Play also advises against device frames.", source = PLAY_SOURCE,
            )
            DeviceClass.PLAY_TABLET_7 -> ShotSpec(
                device, TABLET_CAPTURE, TABLET_STORE, "sevenInchScreenshots", true, 8, 0, required = false,
                advice = "$PLAY_ADVICE Tablets need 4 for large-screen features.", source = PLAY_SOURCE,
            )
            DeviceClass.PLAY_TABLET_10 -> ShotSpec(
                device, TABLET_CAPTURE, TABLET_STORE, "tenInchScreenshots", true, 8, 0, required = false,
                advice = "$PLAY_ADVICE Tablets need 4 for large-screen features.", source = PLAY_SOURCE,
            )
            DeviceClass.PLAY_FEATURE_GRAPHIC -> ShotSpec(
                device, SizeRule.Exact(listOf(PixelSize(1024, 500)), rotatable = false),
                SizeRule.Exact(listOf(PixelSize(1024, 500)), rotatable = false),
                "featureGraphic", true, 1, 0, required = false,
                advice = "1024×500 JPEG or 24-bit RGB PNG without alpha. One graphic per language.",
                source = "Play Console Help 9866151; Android Publisher AppImageType and discovery upload maxSize " +
                    "15728640 (read 2026-10-28); Lingvo intake cap 8388608 bytes",
            )
            // VERIFY: Wear's maximum side; the help page states 384 px minimum and 1:1.
            DeviceClass.PLAY_WEAR -> ShotSpec(
                device, SizeRule.Range(384, 3840, aspects = listOf(PixelSize(1, 1))),
                SizeRule.Range(384, 3840, aspects = listOf(PixelSize(1, 1))),
                "wearScreenshots", true, 8, 1, required = false, requiredWhen = "the app is a Wear OS app",
                source = PLAY_SOURCE,
            )
        }
    }

    fun of(device: DeviceClass): ShotSpec = table.getValue(device)

    fun rule(device: DeviceClass, purpose: ShotPurpose): SizeRule = of(device).let {
        if (purpose == ShotPurpose.STORE) it.storeRule else it.captureRule
    }

    fun accepts(rule: SizeRule, size: PixelSize): Boolean = when (rule) {
        is SizeRule.Exact -> size in rule.sizes || (rule.rotatable && size.rotated() in rule.sizes)
        is SizeRule.Range -> {
            val long = maxOf(size.width, size.height).toLong()
            val short = minOf(size.width, size.height).toLong()
            short >= rule.minSide && long <= rule.maxSide &&
                (rule.maxLongToShort == null || long * rule.maxLongToShort.den <= short * rule.maxLongToShort.num) &&
                (rule.aspects.isEmpty() || rule.aspects.any { aspect ->
                    size.width.toLong() * aspect.height == size.height.toLong() * aspect.width
                })
        }
    }

    /** Every size an [SizeRule.Exact] accepts, rotations included; empty for a range. */
    fun wanted(rule: SizeRule): List<PixelSize> = when (rule) {
        is SizeRule.Exact -> if (rule.rotatable) rule.sizes + rule.sizes.map { it.rotated() } else rule.sizes
        is SizeRule.Range -> emptyList()
    }

    /** "1320×2868, 1290×2796 or 1260×2736 (portrait or landscape)": the sentence an agent reads. */
    fun describe(rule: SizeRule): String = when (rule) {
        is SizeRule.Exact -> joinOr(rule.sizes.map { it.label }) + if (rule.rotatable) " (portrait or landscape)" else ""
        is SizeRule.Range -> buildString {
            append("${rule.minSide}–${rule.maxSide} px on each side")
            rule.maxLongToShort?.let { append(", the long side at most ${ratioLabel(it)} the short side") }
            if (rule.aspects.isNotEmpty()) {
                append(", exactly ")
                append(joinOr(rule.aspects.map { "${it.width}:${it.height}" }))
            }
        }
    }

    /**
     * Every reason [header] cannot serve as [purpose] for [device]; empty means it can.
     *
     * Order: bytes, format, colour model, alpha (store only), then the purpose's size rule.
     */
    fun check(device: DeviceClass, header: ImageHeader, byteSize: Int, purpose: ShotPurpose): List<ShotIssue> {
        val spec = of(device)
        val issues = mutableListOf<ShotIssue>()
        if (byteSize > spec.maxBytes) {
            issues += ShotIssue(
                ShotIssueCode.TOO_LARGE,
                "This file is ${megabytes(byteSize.toLong())}. Screenshots are limited to ${megabytes(spec.maxBytes.toLong())}.",
            )
        }
        val colourOk = if (device == DeviceClass.PLAY_FEATURE_GRAPHIC && purpose == ShotPurpose.STORE) {
            !header.palette && header.channels == 3 && header.bitDepth == 8
        } else (header.palette && header.bitDepth <= 8) || (header.channels in 3..4 && header.bitDepth == 8)
        if (!colourOk) {
            issues += ShotIssue(ShotIssueCode.COLOR_UNSUPPORTED, if (device == DeviceClass.PLAY_FEATURE_GRAPHIC) {
                "Save the feature graphic as 24-bit RGB PNG or JPEG, without alpha."
            } else "Save it as 8-bit RGB. $FORMATS_NOTE")
        }
        if (purpose == ShotPurpose.STORE && header.hasAlpha && !spec.alphaAllowedInStore) {
            issues += ShotIssue(
                ShotIssueCode.ALPHA_NOT_ALLOWED,
                "${device.platform.displayName} refuses images with an alpha channel. Export it as an opaque RGB PNG or a JPEG.",
            )
        }
        val rule = rule(device, purpose)
        val size = PixelSize(header.width, header.height)
        if (!accepts(rule, size)) {
            issues += ShotIssue(
                ShotIssueCode.WRONG_SIZE,
                "This is ${size.label}. ${device.displayName} wants ${describe(rule)}.",
                wanted = wanted(rule),
            )
        }
        return issues
    }

    /** Facts worth saying that do not refuse anything: a capture with alpha is accepted and flattened later. */
    fun warnings(device: DeviceClass, header: ImageHeader, purpose: ShotPurpose): List<ShotIssue> =
        if (purpose == ShotPurpose.CAPTURE && header.hasAlpha && !of(device).alphaAllowedInStore) {
            listOf(
                ShotIssue(
                    ShotIssueCode.ALPHA_NOT_ALLOWED,
                    "This capture has an alpha channel. It is accepted as input; the composed store image will be opaque.",
                ),
            )
        } else {
            emptyList()
        }

    private fun joinOr(items: List<String>): String = when (items.size) {
        0 -> ""
        1 -> items.single()
        else -> items.dropLast(1).joinToString(", ") + " or " + items.last()
    }

    private fun ratioLabel(ratio: Ratio): String = when {
        ratio.den == 1 && ratio.num == 2 -> "twice"
        ratio.den == 1 -> "${ratio.num} times"
        else -> {
            val tenths = ratio.num * 10 / ratio.den
            "${tenths / 10}.${tenths % 10} times"
        }
    }

    private fun megabytes(bytes: Long): String {
        val tenths = bytes * 10 / (1024 * 1024)
        return if (tenths % 10 == 0L) "${tenths / 10} MB" else "${tenths / 10}.${tenths % 10} MB"
    }
}
